import { useNavigate } from "react-router";

import { Button, ButtonStyle, CardGrid, DisplayCard, ShieldIcon } from "@prequel/shared-components";

import { useSources, useSourceVendors } from "@/api/queries/sources";
import Timestamp from "@/components/Timestamp";
import VendorLogo from "@/components/VendorLogo";
import { getReadableVendor, getVendorLogoUrl } from "@/types/common";
import ExistingSource, { getHostOrBucketName } from "@/types/sources";

const SourcesCards = () => {
  const navigate = useNavigate();

  const { data: sources } = useSources();
  const { data: sourceVendors } = useSourceVendors();

  const onCardClick = (id: string) => navigate(`/sources/${id}`);

  if (sources?.length === 0) {
    return (
      <div className="flex-col rounded-sm border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
        <div className="flex">
          <ShieldIcon className="size-12 text-emerald-600" aria-hidden="true" />
          <div className="ml-3">
            <p className="mb-1 font-medium text-gray-900">No configured source.</p>
            <p>{"Click 'Add source' to add your first source."}</p>
          </div>
        </div>
        <div className="mt-3 ml-9">
          <Button text="Add Source" onClick={() => navigate("/sources/new")} type={ButtonStyle.TERTIARY} />
        </div>
      </div>
    );
  }

  return (
    <CardGrid>
      {sources?.map((s: ExistingSource) => (
        <DisplayCard key={s.id} onClick={() => onCardClick(s.id)}>
          <div
            className="
              flex items-center gap-x-4 border-b border-slate-900/5 bg-slate-50 p-6
              group-hover:bg-slate-100
            "
          >
            <VendorLogo logo_url={getVendorLogoUrl(s.vendor, sourceVendors ?? [])} size="md" />
            <div className="text-sm/6 font-medium text-slate-900">
              <div>{s.name}</div>
              <div className="font-mono text-slate-400">{getHostOrBucketName(s)}</div>
            </div>
          </div>
          <dl className="-my-3 divide-y divide-slate-100 px-6 py-4 text-sm/6">
            <div className="flex justify-between gap-x-4 py-3">
              <dt className="text-slate-500">Vendor</dt>
              <dd className="flex items-start gap-x-2">
                <div className="font-medium text-slate-700">{getReadableVendor(s.vendor, sourceVendors ?? [])}</div>
              </dd>
            </div>
            <div className="flex justify-between gap-x-4 py-3">
              <dt className="text-slate-500">Created</dt>
              <dd className="text-slate-700">
                <Timestamp timestamp={s.created_at} />
              </dd>
            </div>
          </dl>
        </DisplayCard>
      ))}
    </CardGrid>
  );
};

export default SourcesCards;
